import { useState, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import adminAPI from './API/adminAPI';
import API from './API.js';
import enterpriseAPI from './API/enterpriseAPI.js';
import { useLocation } from 'react-router';
import { get, ref } from 'firebase/database';
import { db } from '../firebase/firebaseConfig.js';
import { badUsernames } from './badUsernames';
import { useAuth } from './Auth/index.js';
import Logo from '../components/Images/bit-signal-logo.png';
import QRCodeStyling from 'pp-qr-code';

export const useUsernameCheck = (username, enterprise) => {
    const [ response, setResponse ] = useState({ valid: false, message: '' });
    
    const path = enterprise ? `${enterprise}@${username}` : username;

    useEffect(() => {
        const usernameRE = new RegExp(/^[a-zA-Z0-9_-]*$/);

        const handleDebouncedUsernameCheck = debounce(async (username) => {
            if (username) {
                if (username.startsWith('-')) {
                    setResponse(() => ({ valid: false, message: 'Username cannot start with a hyphen' }));
                } else if (username.startsWith('_')) {
                    setResponse(() => ({ valid: false, message: 'Username cannot start with an underscore' }));
                } else if (!usernameRE.test(username)) {
                    setResponse(() => ({ valid: false, message: 'Username can only contain letters, numbers, hyphens, and underscores' }));
                } else if (badUsernames.includes(username)) {
                    setResponse(() => ({ valid: false, message: 'Username is not allowed' }));
                } else if (enterprise) {
                    setResponse(() => ({ valid: true, message: '' }));
                } else {
                    try {
                        const res = await get(ref(db, `/user_uids/${path}`))
                        if (res.exists() && res.val().hasOwnProperty('uid')) {
                            setResponse(() => ({ valid: false, message: 'Username already in use' }));
                        } else {
                            setResponse(() => ({ valid: true, message: '' }));
                        };
                    } catch (err) {
                        setResponse(() => ({ valid: false, message: 'Failed to check username' }));
                    };
                };
            } else {
                setResponse(() => ({ valid: false, message: '' }));
            };
        }, 500);

        if (username) {
            handleDebouncedUsernameCheck(username.toLocaleLowerCase());
        } else {
            setResponse(() => ({ valid: false, message: '' }));
        };

        return () => handleDebouncedUsernameCheck.cancel();
    },[username, response?.valid, setResponse]);

    return useMemo(() => response, [response]);
};

export const useEmailCheck = (email) => {
    const [ response, setResponse ] = useState({ valid: false, complete: false, message: '' });
    
    useEffect(() => {
        const emailRE = new RegExp(/[^@]+@[^@]+\.[^@]+/);
        
        const handleEmailCheck = debounce(async search => {
            try {
                const res = await adminAPI.getUserByEmail(search.toLowerCase().replaceAll(" ",""));
                setResponse(() => res?.data?.message ? { valid: true, complete: true, message: '' } : { valid: false, complete: true, message: 'Email already in use' });
            } catch (err) {
                setResponse(() => ({ valid: false, complete: true, message: 'Failed to check email' }));
            };
        }, 500);

        if (email && emailRE.test(email)) {
            handleEmailCheck(email);
        } else {
            setResponse(() => ({ valid: false, complete: false, message: 'Please use correct format' }));
        };

        return () => handleEmailCheck.cancel();
    },[email, response?.valid, setResponse]);

    return useMemo(() => response, [response]);
};

export const useEnterpriseCheck = (enterprise) => {
    const [ response, setResponse ] = useState({ valid: false, message: '' });

    useEffect(() => {
        const enterpriseRE = new RegExp(/^[A-Za-z0-9-]+$/);
        
        const handleCheckEnterprise = debounce(async () => {
            try {
                const { data } = await adminAPI.getEnterpriseExistsBoolean(enterprise);
                if (data) {
                    setResponse({ valid: false, message: 'Enterprise in use'});
                } else {
                    setResponse({ valid: true, message: ''});
                };
            } catch (err) {
                console.log(err)
                setResponse({ valid: false, message: 'Failed to check enterprise'});
            };
        },300);

        if (enterpriseRE.test(enterprise)) {
            handleCheckEnterprise();
        } else {
            if (enterprise.includes(' ')) {
                setResponse({ valid: false, message: 'Team name cannot include spaces.' });
            } else {
                setResponse({ valid: false, message: 'Team name may only contain alphanumeric characters or hyphens, eg "-".' });
            };
        };

        return () => handleCheckEnterprise.cancel();
    },[enterprise, setResponse]);

    return useMemo(() => response, [response]);
};

export const useQuery = () => {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
};

// export const useFetchLqr = ({ qrId, lastUpdated, route }) => {
//     const [ state, setState ] = useState(null);
//     const [ date, setDate ] = useState(0);
//     const enterprise = useMemo(() => {
//         if (!route || !route?.includes('/')) return '';
//         return route.split('/')[0];
//     }, [route]);

//     useEffect(() => {
//         let isMounted = true;

//         const handleFetchLiquidQr = async () => {
//             try {
//                 if (enterprise) {
//                     const { data } = await enterpriseAPI.getBrandQr(enterprise);
//                     setState({ ...data, data: `${window.location.origin}/${route}`, height: 250, width: 250 });
//                 } else {
//                     if (!qrId || process.env.REACT_APP_LQR === 'false') return;
//                     const { data } = await API.retrieveLiquidQr({ qrId: qrId });
//                     if (!data?.qr_style) return;
//                     setState({ ...data.qr_style, height: 250, width: 250 });
//                 };
//                 setDate(lastUpdated);
//             } catch (err) {
//                 console.error(err);
//             };
//         };

//         if (!qrId && !enterprise) {
//             setState(null);
//             setDate(lastUpdated);
//             return;
//         };

//         if (date !== lastUpdated) handleFetchLiquidQr();

//         return () => {
//             isMounted = false;
//         };
//     },[qrId, enterprise, lastUpdated, date, setState, setDate ]);

//     return useMemo(() => state, [state]);
// };

export const useQrGenerator = ({ qrStyle, route }) => {
    return useMemo(() => {
        if (qrStyle && !qrStyle?.hasOwnProperty('error') && process.env.REACT_APP_LQR !== 'false') {
            return new QRCodeStyling({
                ...qrStyle,
                qrOptions: {
                    errorCorrectionLevel: qrStyle?.image ? 'H' : 'M',
                    mode: 'Byte',
                    typeNumber: 0,
                }
            });
        };
        return new QRCodeStyling({
            data: `https://bitsignal.io/${route}`,
            width: 250,
            height: 250,
            image: Logo,
            dotsOptions: {
                color: '#364957',
                type: 'dots'
            },
            cornersSquareOptions: {
                'type': 'extra-rounded',
                'color': '#364957'
            },
            cornersDotOptions: {
                'type': 'dot',
                'color': '#364957'
            },
            backgroundOptions: {
                'color': '#ffffff'
            },
            qrOptions: {
                errorCorrectionLevel: 'H',
                mode: 'Byte',
                typeNumber: 0,
            },
            imageOptions: {
                hideBackgroundDots:true,
                crossOrigin: 'anonymous',
                margin: 0,
                imageSize: .4
            }
        });
    },[qrStyle, route]);
};

export const usePasswordStrength = (password) => {
    const [data, setData] = useState({
        strength: -1,
        backgroundColor: '#ddd',
        textColor: '#444',
        text: 'Minimum 6 Characters'
    });

    const handleText = (strength) => {
        switch (strength) {
            case 1:
            default:
                return 'Weak';
            case 2:
                return 'Moderate';
            case 3:
                return 'Good';
            case 4:
                return 'Strong';
        };
    };
    const handleColor = (strength) => {
        switch (strength) {
            case 1:
            default:
                return '#f28c28';
            case 2:
                return 'gold';
            case 3:
                return 'green';
            case 4:
                return '#0047ab';
        };
    };
    const handleTextColor = (strength) => {
        switch (strength) {
            case 1:
            case 3:
            case 4:
            default:
                return '#fafafa';
            case 2:
                return '#444';
        };
    };

    useEffect(() => {
        if (password.length >= 6) {
            let strength = 1;
            let backgroundColor = handleColor(strength);
            let textColor = handleTextColor(strength);
            let text = handleText(strength);

            if (/[A-Z]/.test(password)) {
                strength += 1;
                backgroundColor = handleColor(strength);
                textColor = handleTextColor(strength);
                text = handleText(strength);
            }
            if (/[0-9]/.test(password)) {
                strength += 1;
                backgroundColor = handleColor(strength);
                textColor = handleTextColor(strength);
                text = handleText(strength);
            }
            if (/[^A-Za-z0-9]/.test(password)) {
                strength += 1;
                backgroundColor = handleColor(strength);
                textColor = handleTextColor(strength);
                text = handleText(strength);
            }

            setData({ strength, backgroundColor, textColor, text });
        } else if (password.length > 0) {
            setData({
                strength: 0,
                backgroundColor: '#c41e3a',
                textColor: '#fafafa',
                text: 'Not Long Enough'
            });
        } else {
            setData({
                strength: -1,
                backgroundColor: '#ddd',
                textColor: '#444',
                text: 'Minimum 6 Characters'
            });
        }
    }, [password]);

    return useMemo(() => data, [data]);
};

export const useThemes = (userObj) => {
    const [themes, setThemes] = useState([]);

    useEffect(() => {
        let mounted = true;
        const handleGetThemes = async () => {
            try {
                const { data } = await adminAPI.getThemeTemplates();
                if (JSON.stringify(data) !== JSON.stringify(themes)) {
                    mounted && setThemes(data);
                };
            } catch (e) {
                console.log(e);
            };
        };
        
        if (userObj && !userObj?.permissions?.managed_group && !themes?.length) {
            handleGetThemes();
        };

        return () => mounted = false;
    }, [userObj, themes]);

    return useMemo(() => themes, [themes]);
};

export const useLocalStorage = (key, initialValue) => {

    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.log(error);
            return initialValue;
        }
    });
  
    useEffect(() => {
        try {
            if (storedValue === null) {
                window.localStorage.removeItem(key);
            } else {
                window.localStorage.setItem(key, JSON.stringify(storedValue));
            }
        } catch (error) {
            console.log(error);
        }
    }, [key, storedValue]);
  
    return [storedValue, setStoredValue];
};

export const useEnterpriseThemes = (userObj) => {
    const [ state, setState ] = useState({ complete: false, themes: null });
    useEffect(() => {
        if (!userObj || state.themes !== null) return;
        
        const handleGetEntThemes = async () => {
            if (!userObj || !userObj?.enterprise) {
                setState({ complete: true, themes: [] });
                return;
            };

            try {
                const { data } = await enterpriseAPI.getEnterpriseBrands();
                setState({ complete: true, themes: data });
            } catch (err) {
                console.error(err);
                setState({ complete: true, themes: [] });
            };
        };

        if (state.themes === null) {
            handleGetEntThemes();
        };
    },[userObj, userObj?.enterprise, state.themes, setState]);

    return useMemo(() => state.themes, [state.themes]);
};

export const useEnterpriseAccount = (userObj) => {
    const [ state, setState ] = useState({ complete: false, account: null });

    useEffect(() => {
        if (!userObj || state.account !== null) return;

        const handleGetEntAccount = async () => {
            if (!userObj?.enterprise) {
                setState({ complete: true, account: {} });
                return;
            };

            try {
                const { data } = await enterpriseAPI.getEnterpriseAccount(userObj?.uid);
                if (data) {
                    setState({ complete: true, account: data });
                };
            } catch (err) {
                console.error(err);
                setState({ complete: true, account: {} });
            };
        };

        if (state.account === null) {
            handleGetEntAccount();
        };
    },[userObj, userObj?.enterprise, state.account, setState]);

    return useMemo(() => state.account, [state.account]);
};


export const useUserAnalytics = (uid) => {
    const [state, setState] = useState({
        card_views: {},
        downloads: {},
        loading: true,
        user_login: {}
    });

    useEffect(() => {
        const handleGetUserAnalytics = async () => {
            try {
                const { data } = await API.getUserAnalytics(uid);
                if (data) {
                    setState(prev => ({
                        ...prev,
                        ...data,
                        loading: false
                    }));
                };
            } catch (err) {
                console.error(err);
                setState(prev => ({ ...prev, loading: false }));
            };
        };
        if (state.loading) handleGetUserAnalytics();
    },[]);
    
    return useMemo(() => state, [state]);
};

export const useThreads = () => {
    const { messageObj } = useAuth();
    const location = useLocation();
    const [ state, setState ] = useState({
        view: 'inbox',
        state: location.state || {userToMessage: '', thread: '', type: ''},
        currentMessage: {},
        inbox: [],
        contacts: [],
        deleted: []
    });

    useEffect(()=> {
        let mounted = true;
        const handleDeleteMessages = async (data) => {
            try {
                mounted && await API.deleteMessage(data);
            } catch (err) {
                console.error(err);
            };
        };

        if (messageObj) {
            // Deletes messages passed the delete window
            const arr = [];
            Object.values(messageObj).forEach(thread => {
                if (thread.deleted) {
                    if (Date.now() > thread.deleted) {
                        arr.push(thread.key);
                    };
                };
            });
            
            if (arr.length > 0) handleDeleteMessages({arr: arr});
            
            // Sets message array states for each mailbox
            mounted && setState(prev => ({
                ...prev,
                inbox: Object.values(messageObj).filter(thread => thread.deleted === 0 && thread.type === 'message'),
                contacts: Object.values(messageObj).filter(thread => thread.deleted === 0 && thread.type === 'contact'),
                deleted: Object.values(messageObj).filter(thread => thread.deleted > 0)
            }));
        } else {
            mounted && setState({
                view: 'inbox',
                state: location.state || {userToMessage: '', thread: '', type: ''},
                currentMessage: {},
                inbox: [],
                contacts: [],
                deleted: []
            });
        };

        return () => {
            mounted = false;
        };
    },[messageObj, location.state]);

    return useMemo(() => [state, setState], [state]);
};

export const useContacts = () => {
    const [ state, setState ] = useState({
        contacts: null,
        last: '',
        orderBy: 'none',
        searched: null,
        searchType: 'name',
    });

    useEffect(() => {
        let mounted = true;
        const handleGetContacts = async () => {
            try {
                const res = await API.getContacts("");
                if (res) {
                    mounted && setState(prev => ({
                        ...prev,
                        contacts: res,
                        last: res[res.length - 1].vcard.name_last,
                        searched: res
                    }));
                } else {
                    mounted && setState(prev => ({
                        ...prev,
                        contacts: [],
                        last: ''
                    }));
                };
            } catch (err) {
                console.error(err);
            };
        };
    
        if (!state.contacts) {
            handleGetContacts();
        };
    
        return () => {
            mounted = false;
            state.contacts?.forEach(contact => {
                if (contact.vcf) {
                    URL.revokeObjectURL(contact.vcf);
                };
            });
        };
    },[]);

    return useMemo(() => [state, setState], [state]);
};

export const useGetNetwork = (networkId) => {
    const [ state, setState ] = useState({
        id: '',
        data: {}
    });

    useEffect(() => {
        let mounted = true;
        const handleGetNetwork = async () => {
            try {
                const { data } = await API.getNetworkObject(networkId);
                if (data) {
                    mounted && setState(() => ({ id: networkId, data: data }));
                };
            } catch (err) {
                console.error(err);
            };
        };
        
        if (networkId && state.id !== networkId) {
            handleGetNetwork();
        };

        return () => mounted = false;
    },[state.id, networkId]);

    return useMemo(() => state.data,[state.data]);
};